import { apiService } from './apiService';

export type TaskStatus = 'new' | 'inProgress' | 'blocked' | 'readyForReview' | 'done';

export interface TaskManagerStory {
  id: string;
  title: string;
}

export interface TaskManagerTask {
  id: string;
  storyId: string;
  title: string;
  description: string;
  path: string;
  status: TaskStatus;
  assigneeId?: string;
  assigneeName?: string;
  dueDate?: string;
  comments: TaskManagerTaskComment[];
}

export interface TaskManagerTaskComment {
  id: string;
  taskId: string;
  userId: string;
  userName: string;
  comment: string;
  createdAt: string;
}

export interface TaskManagerBoard {
  id: string;
  munkaszam: string;
  munkaTargya: string;
  createdAt: string;
  stories: TaskManagerStory[];
  tasks: TaskManagerTask[];
}

export interface TaskManagerBoardListItem {
  id: string;
  munkaszam: string;
  munkaTargya: string;
  createdAt: string;
  newCount: number;
  inProgressCount: number;
  doneCount: number;
  taskFilters: Array<{
    assigneeUserId?: string;
    dueDate?: string;
  }>;
}

const mapBoardListItem = (item: any): TaskManagerBoardListItem => ({
  id: String(item.id),
  munkaszam: item.munkaszam || '',
  munkaTargya: item.munkaTargya || '',
  createdAt: item.createdAt || new Date().toISOString(),
  newCount: Number(item.newCount || 0),
  inProgressCount: Number(item.inProgressCount || 0),
  doneCount: Number(item.doneCount || 0),
  taskFilters: Array.isArray(item.taskFilters)
    ? item.taskFilters.map((task: any) => ({
        assigneeUserId: task.assigneeUserId ? String(task.assigneeUserId) : undefined,
        dueDate: task.dueDate || undefined,
      }))
    : [],
});

const mapBoard = (item: any): TaskManagerBoard => ({
  id: String(item.id),
  munkaszam: item.munkaszam || '',
  munkaTargya: item.munkaTargya || '',
  createdAt: item.createdAt || new Date().toISOString(),
  stories: Array.isArray(item.stories)
    ? item.stories.map((story: any) => ({
        id: String(story.id),
        title: story.title || '',
      }))
    : [],
  tasks: Array.isArray(item.tasks)
    ? item.tasks.map((task: any) => ({
        id: String(task.id),
        storyId: String(task.storyId),
        title: task.title || '',
        description: task.description || '',
        path: task.path || '',
        status: (task.status || 'new') as TaskStatus,
        assigneeId: task.assigneeUserId ? String(task.assigneeUserId) : undefined,
        assigneeName: task.assigneeName || undefined,
        dueDate: task.dueDate || undefined,
        comments: Array.isArray(task.comments)
          ? task.comments.map((comment: any) => ({
              id: String(comment.id),
              taskId: String(comment.taskId),
              userId: String(comment.userId || ''),
              userName: comment.userName || '',
              comment: comment.comment || '',
              createdAt: comment.createdAt || new Date().toISOString(),
            }))
          : [],
      }))
    : [],
});

export const taskManagerService = {
  async getBoards(): Promise<TaskManagerBoardListItem[]> {
    const response = await apiService.get('/TaskManager/boards');
    const data = Array.isArray(response?.data) ? response.data : [];
    return data.map(mapBoardListItem);
  },

  async getBoardById(boardId: string): Promise<TaskManagerBoard> {
    const response = await apiService.get(`/TaskManager/boards/${boardId}`);
    return mapBoard(response?.data);
  },

  async createBoard(munkaszamId: string): Promise<TaskManagerBoard> {
    const response = await apiService.post('/TaskManager/boards', {
      munkaszamId: Number(munkaszamId),
    });
    return mapBoard(response?.data);
  },

  async createStory(boardId: string, title: string): Promise<void> {
    await apiService.post(`/TaskManager/boards/${boardId}/stories`, { title });
  },

  async createTask(
    boardId: string,
    payload: { storyId: string; title: string; description: string; status: TaskStatus; assigneeId?: string }
  ): Promise<void> {
    await apiService.post(`/TaskManager/boards/${boardId}/tasks`, {
      storyId: Number(payload.storyId),
      title: payload.title,
      description: payload.description,
      status: payload.status,
      assigneeUserId: payload.assigneeId || null,
    });
  },

  async updateTask(
    boardId: string,
    taskId: string,
    payload: { storyId: string; title: string; description: string; status: TaskStatus; assigneeId?: string }
  ): Promise<void> {
    await apiService.put(`/TaskManager/boards/${boardId}/tasks/${taskId}`, {
      storyId: Number(payload.storyId),
      title: payload.title,
      description: payload.description,
      status: payload.status,
      assigneeUserId: payload.assigneeId || null,
    });
  },

  async moveTask(boardId: string, taskId: string, storyId: string, status: TaskStatus): Promise<void> {
    await apiService.patch(`/TaskManager/boards/${boardId}/tasks/${taskId}/move`, {
      storyId: Number(storyId),
      status,
    });
  },

  async assignTask(boardId: string, taskId: string, assigneeId: string): Promise<void> {
    await apiService.patch(`/TaskManager/boards/${boardId}/tasks/${taskId}/assign`, {
      assigneeUserId: assigneeId || null,
    });
  },

  async deleteBoard(boardId: string): Promise<void> {
    await apiService.delete(`/TaskManager/boards/${boardId}`);
  },

  async deleteStory(boardId: string, storyId: string): Promise<void> {
    await apiService.delete(`/TaskManager/boards/${boardId}/stories/${storyId}`);
  },

  async deleteTask(boardId: string, taskId: string): Promise<void> {
    await apiService.delete(`/TaskManager/boards/${boardId}/tasks/${taskId}`);
  },

  async getTaskComments(boardId: string, taskId: string): Promise<TaskManagerTaskComment[]> {
    const response = await apiService.get(`/TaskManager/boards/${boardId}/tasks/${taskId}/comments`);
    const data = Array.isArray(response?.data) ? response.data : [];

    return data.map((comment: any) => ({
      id: String(comment.id),
      taskId: String(comment.taskId),
      userId: String(comment.userId || ''),
      userName: comment.userName || '',
      comment: comment.comment || '',
      createdAt: comment.createdAt || new Date().toISOString(),
    }));
  },

  async addTaskComment(boardId: string, taskId: string, comment: string): Promise<void> {
    await apiService.post(`/TaskManager/boards/${boardId}/tasks/${taskId}/comments`, {
      comment,
    });
  },

  async updateTaskComment(boardId: string, taskId: string, commentId: string, comment: string): Promise<void> {
    await apiService.put(`/TaskManager/boards/${boardId}/tasks/${taskId}/comments/${commentId}`, {
      comment,
    });
  },

  async deleteTaskComment(boardId: string, taskId: string, commentId: string): Promise<void> {
    await apiService.delete(`/TaskManager/boards/${boardId}/tasks/${taskId}/comments/${commentId}`);
  },
};
